/**
 * Hides a page's hand-rolled cookie banner in the preview.
 *
 * The CMP registry (cmp-consent-hiders) covers the providers a page embeds:
 * each has stable ids or classes that CSS can target. A banner the site
 * built itself carries none — tryriot.com's is a fixed <div> with only
 * Tailwind utility classes ("fixed bg-white rounded-ds-20 max-w-[440px]"),
 * inside a plain root <div>: nothing a selector could name that would not
 * name half the page too. Such banners are recognized by what they are
 * rather than by how they are labelled: a fixed element, short, that talks
 * about cookies and offers an accept/reject/settings control.
 *
 * Narrow on purpose:
 * - only `position: fixed` elements, so nothing in the document flow can be
 *   removed — a section on cookies stays;
 * - shallow (`MAX_DEPTH` levels under <body>): banners mount at the root,
 *   while the cookie *policy* page, a footer link or a blog post live deep in
 *   the layout;
 * - short text (`MAX_TEXT_LENGTH`): a fixed page wrapper (scroll-jacking
 *   layouts) also "mentions cookies" somewhere in its thousands of characters,
 *   and must not be hidden;
 * - and an explicit consent control, so a fixed cookie *notice* link or a
 *   sticky footer that merely mentions cookies is left alone.
 *
 * Hidden in place (inline `display: none !important`), the outermost
 * qualifying element first — a modal's fixed backdrop wrapper contains its
 * card's text, qualifies itself, and takes the card with it. Idempotent: a
 * hidden element is marked and skipped on the next pass (the load handler
 * polls while the page streams in).
 */

const MAX_DEPTH = 5;
const MAX_TEXT_LENGTH = 1500;
const HIDDEN_ATTR = 'data-tinct-cookie-banner';

const COOKIE_WORD = /\bcookies?\b/i;
// Accept / reject, in the languages the demo prospects write in. Not the
// "manage" / "settings" kind: a fixed nav with a "Cookie policy" link and a
// "Settings" entry must not qualify.
const CONSENT_CONTROL =
  /^\s*(accept|agree|allow|reject|decline|refuse|deny|got it|ok(ay)?|i (understand|agree)|tout accepter|accepter|j'accepte|refuser|tout refuser|alle akzeptieren|akzeptieren|ablehnen|aceptar|rechazar|accetta|rifiuta)\b/i;

const isConsentControl = (el: Element): boolean =>
  CONSENT_CONTROL.test((el as HTMLElement).innerText ?? el.textContent ?? '');

const hasConsentControl = (el: Element): boolean =>
  Array.from(el.querySelectorAll('button, a, [role="button"]')).some(
    isConsentControl,
  );

const isCookieBanner = (el: HTMLElement, win: Window): boolean => {
  if (el.hasAttribute(HIDDEN_ATTR) || el.closest('[data-tinct-overlay]'))
    return false;
  if (win.getComputedStyle(el).position !== 'fixed') return false;
  const text = el.innerText ?? el.textContent ?? '';
  if (text.length > MAX_TEXT_LENGTH || !COOKIE_WORD.test(text)) return false;
  return hasConsentControl(el);
};

export function hideUnlabelledCookieBanners(doc: Document): void {
  const win = doc.defaultView;
  const body = doc.body;
  if (!win || !body) return;

  // Breadth-first over the first levels under <body>; a hidden element's
  // subtree is not descended (its card would qualify again, needlessly).
  let level: Element[] = Array.from(body.children);
  for (let depth = 0; depth < MAX_DEPTH && level.length > 0; depth++) {
    const next: Element[] = [];
    for (const el of level) {
      if (!(el instanceof win.HTMLElement)) continue;
      if (isCookieBanner(el, win)) {
        el.style.setProperty('display', 'none', 'important');
        el.setAttribute(HIDDEN_ATTR, '');
        continue;
      }
      if (!el.hasAttribute(HIDDEN_ATTR)) next.push(...Array.from(el.children));
    }
    level = next;
  }
}
